import FetchDocumentTypeSections from '@Application/Organizations/Workflows/Queries/FetchDocumentTypeSections';
import fetchDocumentTypeSections from '@Application/Organizations/Workflows/fetchDocumentTypeSections';
import AdvancedSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/AdvancedSection';
import DocumentSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/DocumentSection';
import InputsSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/InputsSection';
import PromptsSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/PromptsSection';
import ReevaluationSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/ReevaluationSection';
import SplitSection from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/SplitSection';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import useWorkflowDraft from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Providers/WorkflowDraftProvider/useWorkflowDraft';
import { closeDocumentSettingsScreen } from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Signals/documentSettingsScreenSignal';
import BackLink from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/BackLink';
import ScreenHeading from '@UI/Pages/Administration/DocumentGroups/Shared/ScreenHeading';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { css } from '@emotion/react';
import * as React from 'react';
import { useToast } from '@verclam/toast-v2';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Form, type FormStyle } from '@verclam/ui-kit';

const formStyle = {
	'--ui-kit-form-gap': '1.25rem',
	maxWidth: '53.75rem',
	width: '100%',
} as FormStyle;

const footerStyle = css`
	display: flex;
	justify-content: flex-end;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly stageIndex: number;
	readonly stepIndex: number;
};

type FormProps = Props & {
	/** The document as it stands in the group draft when the screen opens. */
	readonly document: DocumentSettingsFormData;
};

/**
 * The settings of one document, edited on a copy: "Appliquer" writes the copy back into the group
 * draft, "Annuler" and the back link drop it. Mounted only while the screen is open and keyed on the
 * document's position, so that each opening starts from the draft's current document.
 */
const DocumentSettingsForm: React.FC<FormProps> = ({ stageIndex, stepIndex, document }) => {
	const dispatch = useDispatch();
	const toast = useToast();
	const { draft, setDraft } = useWorkflowDraft();
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);
	const catalog = useSelector((state) => state.organizationWorkflowsStore.catalog);
	const [settings, setSettings] = React.useState(document);
	const formId = React.useId();

	const documentTypeId = document.documentTypeId;
	const documentType = documentTypes?.findById(documentTypeId) ?? null;
	const documentTypeLabel = documentType?.label ?? 'Type inconnu';
	const preset = catalog?.findForDocumentType(documentType?.key ?? null) ?? null;
	const displayedLabel = settings.label.trim() === '' ? documentTypeLabel : settings.label;

	const {
		data: loadedSections,
		error: sectionsError,
		refetch: refetchSections,
	} = useStoreQuery({
		selector: (state) => state.organizationWorkflowsStore.sections,
		stopOn: (value) =>
			organizationId === null ||
			documentTypeId === null ||
			(!!value && value.organizationId === organizationId && value.documentTypeId === documentTypeId),
		query: () => {
			if (organizationId === null || documentTypeId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchDocumentTypeSections(new FetchDocumentTypeSections(organizationId, documentTypeId)),
			);
		},
		deps: [organizationId, documentTypeId],
	});

	const sections =
		loadedSections !== null &&
		loadedSections.organizationId === organizationId &&
		loadedSections.documentTypeId === documentTypeId
			? loadedSections
			: null;

	const [removedDependencyCount, setRemovedDependencyCount] = React.useState(0);
	const hasCheckedDependencies = React.useRef(false);

	React.useEffect(() => {
		if (hasCheckedDependencies.current || sections === null || !sections.hasTemplate) {
			return;
		}

		hasCheckedDependencies.current = true;
		const cleaned = settings.retainKnownSectionDependencies(sections.sections);
		setRemovedDependencyCount(settings.sectionDependencyCount - cleaned.sectionDependencyCount);
		setSettings(cleaned);
	}, [sections, settings]);

	const isTakenOver = documentTypeId !== null && draft.automaticDocumentTypeIds.includes(documentTypeId);
	const isSupportedByTemplate =
		sections === null ||
		!sections.hasTemplate ||
		sections.sections.some((section) => section.isReevaluation);

	const groupName = draft.label.trim() === '' ? 'Nouveau groupe' : draft.label;

	const handleSubmit = React.useCallback(() => {
		if (!settings.isFormDataValid()) {
			setSettings((current) => current.showErrors());
			return;
		}

		setDraft((current) => current.replaceDocument(stageIndex, stepIndex, settings));
		closeDocumentSettingsScreen();
		toast.current?.show({
			severity: 'info',
			summary: 'Réglages appliqués. Ils seront publiés à l’enregistrement du groupe.',
		});
	}, [settings, setDraft, stageIndex, stepIndex, toast]);

	return (
		<Form
			id={formId}
			onSubmit={handleSubmit}
			style={formStyle}
			aria-label={`Réglages du document ${documentTypeLabel}`}
		>
			<BackLink label={groupName} onClick={closeDocumentSettingsScreen} />
			<ScreenHeading
				eyebrow={`${groupName} · Étape ${stageIndex + 1} · Réglages du document`}
				title={displayedLabel}
			/>
			<DocumentSection
				settings={settings}
				documentTypeLabel={documentTypeLabel}
				onChange={setSettings}
			/>
			<InputsSection settings={settings} stageIndex={stageIndex} onChange={setSettings} />
			<PromptsSection settings={settings} preset={preset} onChange={setSettings} />
			<ReevaluationSection
				settings={settings}
				preset={preset}
				isTakenOver={isTakenOver}
				isSupportedByTemplate={isSupportedByTemplate}
				onChange={setSettings}
			/>
			<SplitSection
				settings={settings}
				sections={sections}
				sectionsError={sectionsError}
				removedDependencyCount={removedDependencyCount}
				onRetry={refetchSections}
				onChange={setSettings}
			/>
			<AdvancedSection settings={settings} onChange={setSettings} />
			<div css={footerStyle}>
				<Button
					type="button"
					severity="secondary"
					label="Annuler"
					onClick={closeDocumentSettingsScreen}
				/>
				<Button type="submit" label="Appliquer" />
			</div>
		</Form>
	);
};

/** Finds the document the screen is opened on; nothing renders if the draft no longer holds it. */
const DocumentSettings: React.FC<Props> = ({ stageIndex, stepIndex }) => {
	const { draft } = useWorkflowDraft();
	const document = draft.findDocument(stageIndex, stepIndex);

	if (document === null) {
		return null;
	}

	return (
		<DocumentSettingsForm
			key={`${stageIndex}-${stepIndex}`}
			stageIndex={stageIndex}
			stepIndex={stepIndex}
			document={document}
		/>
	);
};

export default DocumentSettings;
